
import React, { useState, useEffect } from 'react';
import { couponService } from '../services/couponService';
import { Coupon, CouponType } from '../types';
import { 
  Tag, 
  Plus, 
  Trash2, 
  Calendar, 
  Ticket, 
  CheckCircle, 
  Loader2, 
  X, 
  AlertCircle,
  Zap,
  Clock,
  DollarSign,
  TrendingUp,
  Percent
} from 'lucide-react';

interface Props {
  storeId: string;
}

const AdminCoupons: React.FC<Props> = ({ storeId }) => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [formData, setFormData] = useState({
    code: '',
    type: 'PERCENTAGE' as CouponType,
    value: 10,
    minOrderValue: 0,
    expiryDate: '',
    maxUsage: 100,
    isActive: true
  });

  useEffect(() => {
    loadCoupons();
  }, [storeId]);

  const loadCoupons = () => {
    setIsLoading(true);
    setCoupons(couponService.getStoreCoupons(storeId));
    setIsLoading(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    couponService.createCoupon({ ...formData, storeId });
    setShowModal(false);
    loadCoupons();
    setFormData({ code: '', type: 'PERCENTAGE', value: 10, minOrderValue: 0, expiryDate: '', maxUsage: 100, isActive: true });
  };

  return (
    <div className="space-y-10 animate-in fade-in duration-700 pb-20">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight">Coupon Factory</h1>
          <p className="text-gray-400 text-sm font-bold uppercase tracking-widest">Promotion & Discount Engine</p>
        </div>
        <button 
          onClick={() => setShowModal(true)}
          className="bg-blue-600 text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-blue-100 hover:bg-blue-700 transition-all flex items-center gap-2"
        >
          <Plus className="h-4 w-4" /> Create New Coupon
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
         <div className="lg:col-span-2 space-y-6">
            {coupons.length === 0 ? (
              <div className="bg-white p-20 rounded-[40px] border border-dashed border-gray-200 text-center space-y-4 opacity-50">
                 <Ticket className="h-16 w-16 mx-auto text-gray-300" />
                 <p className="font-black uppercase tracking-widest text-xs">No active coupons. Create one to drive more sales!</p>
              </div>
            ) : (
              coupons.sort((a,b) => b.id.localeCompare(a.id)).map((c) => (
                <div key={c.id} className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-xl flex flex-col md:flex-row justify-between items-center gap-6 hover:border-blue-200 transition-all group">
                   <div className="flex items-center gap-6">
                      <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center">
                         <Tag className="h-8 w-8" />
                      </div>
                      <div>
                         <div className="flex items-center gap-3">
                            <h3 className="text-xl font-black text-gray-900 tracking-wider font-mono">{c.code}</h3>
                            <span className="bg-emerald-50 text-emerald-600 text-[8px] font-black px-3 py-1 rounded-full uppercase tracking-widest border border-emerald-100">
                               {c.type === 'PERCENTAGE' ? `${c.value}% OFF` : `$${c.value} OFF`}
                            </span>
                         </div>
                         <div className="flex items-center gap-4 mt-2 text-xs font-bold text-gray-400 uppercase tracking-widest">
                            <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> Exp: {new Date(c.expiryDate).toLocaleDateString()}</span>
                            <span className="flex items-center gap-1.5"><CheckCircle className="h-3.5 w-3.5" /> Used {c.usageCount} times</span>
                         </div>
                         <p className="text-[10px] text-gray-400 mt-2 font-bold uppercase tracking-widest">Min Order: ${c.minOrderValue}</p>
                      </div>
                   </div>
                   <button 
                     onClick={() => { couponService.deleteCoupon(c.id); loadCoupons(); }}
                     className="p-4 bg-gray-50 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-2xl transition-all"
                   >
                      <Trash2 className="h-5 w-5" />
                   </button>
                </div>
              ))
            )}
         </div>

         <div className="space-y-8">
            <div className="bg-gray-900 p-10 rounded-[40px] text-white shadow-2xl relative overflow-hidden group">
               <div className="absolute top-0 right-0 p-8 opacity-20 group-hover:scale-110 transition-transform"><Zap className="h-24 w-24" /></div>
               <div className="relative space-y-6">
                  <h3 className="text-xl font-black tracking-tight">Growth Hacks</h3>
                  <p className="text-gray-400 text-sm leading-relaxed font-medium">Bhai, small coupons like 'WELCOME10' boost your newsletter signups by 60%.</p>
                  <div className="space-y-4 pt-4 border-t border-white/20">
                     <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center"><CheckCircle className="h-4 w-4" /></div>
                        <p className="text-xs font-bold uppercase tracking-widest text-blue-400">Cart Recovery Codes</p>
                     </div>
                     <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center"><CheckCircle className="h-4 w-4" /></div>
                        <p className="text-xs font-bold uppercase tracking-widest text-blue-400">Limited Usage Urgency</p>
                     </div>
                  </div>
               </div>
            </div>
         </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-[200] bg-gray-950/80 backdrop-blur-xl flex items-center justify-center p-4">
           <div className="bg-white w-full max-w-2xl rounded-[40px] shadow-2xl overflow-hidden animate-in zoom-in duration-300">
              <div className="p-8 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                 <h3 className="text-2xl font-black text-gray-900 tracking-tight">Generate Promo Code</h3>
                 <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-full transition-all"><X className="h-6 w-6 text-gray-400" /></button>
              </div>
              <form onSubmit={handleSubmit} className="p-10 space-y-8">
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Code (e.g. LYNQ20)</label>
                       <input required value={formData.code} onChange={e => setFormData({...formData, code: e.target.value.toUpperCase()})} placeholder="UNIQUE_CODE" className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-black font-mono" />
                    </div>
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Type</label>
                       <div className="flex bg-gray-100 p-1 rounded-2xl">
                          <button type="button" onClick={() => setFormData({...formData, type: 'PERCENTAGE'})} className={`flex-1 py-3 rounded-xl text-[10px] font-black uppercase transition-all ${formData.type === 'PERCENTAGE' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-400'}`}>Percentage</button>
                          <button type="button" onClick={() => setFormData({...formData, type: 'FIXED'})} className={`flex-1 py-3 rounded-xl text-[10px] font-black uppercase transition-all ${formData.type === 'FIXED' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-400'}`}>Fixed Amt</button>
                       </div>
                    </div>
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Value {formData.type === 'PERCENTAGE' ? '(%)' : '($)'}</label>
                       <input type="number" required value={formData.value} onChange={e => setFormData({...formData, value: Number(e.target.value)})} className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" />
                    </div>
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Min Order Value ($)</label>
                       <input type="number" required value={formData.minOrderValue} onChange={e => setFormData({...formData, minOrderValue: Number(e.target.value)})} className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" />
                    </div>
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Expiry Date</label>
                       <input type="date" required value={formData.expiryDate} onChange={e => setFormData({...formData, expiryDate: e.target.value})} className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" />
                    </div>
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Max Usage Limits</label>
                       <input type="number" value={formData.maxUsage} onChange={e => setFormData({...formData, maxUsage: Number(e.target.value)})} className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" />
                    </div>
                 </div>

                 <button 
                   type="submit" 
                   className="w-full py-6 bg-blue-600 text-white rounded-[2rem] font-black text-lg shadow-2xl hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
                 >
                    Activate Coupon <Tag className="h-5 w-5" />
                 </button>
              </form>
           </div>
        </div>
      )}
    </div>
  );
};

export default AdminCoupons;
